import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import SiteRowActionMenu from '../SiteRowActionMenu';
import { Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { AdminPageHeader } from '../ui';
import SiteService from '../../../services/siteService';
import useSiteListFilters from '../../../hooks/useSiteListFilters';
import SiteListStats from '../../sites/SiteListStats';
import SiteListFilters from '../../sites/SiteListFilters';
import SiteCareModeBadge from '../../sites/SiteCareModeBadge';
import {
  buildSitePayload,
  CARE_MODE_OPTIONS,
  EMPTY_SITE_FORM,
  isCentralSite,
  siteFormFromRecord
} from '../../sites/siteListUtils';
import { SITE_CARE_MODES } from '../../../constants/siteCareModes';
import '../../sites/SiteCareModeBadge.css';
import '../CompanyManagement.css';
import './AdminSitesPage.css';

export default function AdminSitesPage() {
  const { toast } = useOutletContext() || { toast: () => {} };
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState(EMPTY_SITE_FORM);
  const [actionMenuSite, setActionMenuSite] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);

  const loadSites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await SiteService.getAllSites({ limit: 500, offset: 0, active_only: false });
      setSites(res.success ? res.data || [] : []);
    } catch (err) {
      toast('danger', err.message || 'Chargement des sites impossible.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    careModeFilter,
    setCareModeFilter,
    filteredSites,
    stats,
    hasActiveFilters,
    clearFilters
  } = useSiteListFilters(sites);

  const closeActionMenu = useCallback(() => {
    setActionMenuSite(null);
    setActionMenuAnchor(null);
  }, []);

  const openActionMenu = (event, site) => {
    if (actionMenuSite?.id === site.id && actionMenuAnchor) {
      closeActionMenu();
      return;
    }
    setActionMenuAnchor(event.currentTarget);
    setActionMenuSite(site);
  };

  const openCreateModal = () => {
    setEditingSite(null);
    setFormData(EMPTY_SITE_FORM);
    setShowModal(true);
  };

  const openEditModal = (site) => {
    setEditingSite(site);
    setFormData(siteFormFromRecord(site));
    setShowModal(true);
    closeActionMenu();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name || formData.name.trim().length < 2) {
      toast('warning', 'Le nom du site est obligatoire (2 caractères minimum).');
      return;
    }

    setSaving(true);
    try {
      const payload = buildSitePayload(formData);
      const res = editingSite
        ? await SiteService.updateSite(editingSite.id, payload)
        : await SiteService.createSite(payload);
      if (!res.success) {
        toast('danger', res.message || 'Enregistrement impossible.');
        return;
      }
      toast(
        'success',
        editingSite
          ? `« ${formData.name.trim()} » a été mis à jour.`
          : `Site « ${formData.name.trim()} » créé.`
      );
      setShowModal(false);
      await loadSites();
    } catch (err) {
      toast('danger', err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (site) => {
    try {
      if (site.is_active) {
        await SiteService.deactivateSite(site.id);
        toast('success', `« ${site.name} » a été désactivé.`);
      } else {
        await SiteService.updateSite(site.id, { is_active: true });
        toast('success', `« ${site.name} » a été réactivé.`);
      }
      closeActionMenu();
      await loadSites();
    } catch (err) {
      toast('danger', err.message || 'Mise à jour du statut impossible.');
    }
  };

  const handleDelete = async (site) => {
    if (isCentralSite(site)) return;
    if (!window.confirm(`Supprimer définitivement le site « ${site.name} » ?`)) return;
    try {
      await SiteService.deleteSite(site.id);
      toast('success', `Site « ${site.name} » supprimé.`);
      closeActionMenu();
      await loadSites();
    } catch (err) {
      toast('danger', err.message || 'Suppression impossible.');
    }
  };

  const pharmacyLocked = formData.site_care_mode === SITE_CARE_MODES.WITH_PHARMACY;
  const hasSites = filteredSites.length > 0;

  return (
    <div className="adm-dash site-management-v2">
      <AdminPageHeader
        badge="Administration"
        title="Gestion des sites"
        description="Répertoire des sites SMIE — modes de prise en charge, pharmacie locale et statut opérationnel."
        actions={
          <button type="button" className="co-create-btn" onClick={openCreateModal}>
            <Icon name="plus" size={14} />
            <span>Nouveau site</span>
          </button>
        }
      />

      <section className="co-shell adm-panel">
        <SiteListStats stats={stats} />

        <SiteListFilters
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          careModeFilter={careModeFilter}
          onCareModeFilterChange={setCareModeFilter}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
          onRefresh={loadSites}
          loading={loading}
          filteredCount={filteredSites.length}
          repositoryTotal={sites.length}
        />

        {loading ? (
          <div className="co-state">
            <Spinner animation="border" variant="primary" />
            <p>Chargement du répertoire des sites…</p>
          </div>
        ) : sites.length === 0 ? (
          <div className="co-state co-state--empty">
            <h3>Aucun site enregistré</h3>
            <p>Créez le premier site de prise en charge SMIE.</p>
            <button type="button" className="co-create-btn" onClick={openCreateModal}>
              <Icon name="plus" size={14} />
              <span>Créer un site</span>
            </button>
          </div>
        ) : !hasSites ? (
          <div className="co-state">
            <h6>Aucun résultat</h6>
            <p>Modifiez la recherche ou réinitialisez les filtres.</p>
            {hasActiveFilters ? (
              <button type="button" className="co-list-clear-btn mt-2" onClick={clearFilters}>
                Réinitialiser les filtres
              </button>
            ) : null}
          </div>
        ) : (
          <div
            className="co-table-wrap co-list-table-wrap adm-mobile-table-wrap"
            key={`${search}-${statusFilter}-${careModeFilter}`}
          >
            <table className="co-table co-table--compact co-table--sites adm-mobile-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Contact</th>
                  <th className="co-table__actions-col text-end" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredSites.map((site) => {
                  const central = isCentralSite(site);
                  const hasPharmacy =
                    site.has_local_pharmacy || site.site_care_mode === SITE_CARE_MODES.WITH_PHARMACY;
                  return (
                    <tr
                      key={site.id}
                      className={actionMenuSite?.id === site.id ? 'adm-table-row--menu-open' : ''}
                    >
                      <td className="adm-mobile-card-head" data-label="Site">
                        <div className="st-site-cell">
                          <div className="st-site-name-row">
                            <span className={`st-site-name ${site.is_active ? '' : 'st-site-name--inactive'}`}>
                              {site.name}
                            </span>
                            <span
                              className={`st-status-dot ${site.is_active ? 'is-active' : 'is-inactive'}`}
                              title={site.is_active ? 'Actif' : 'Inactif'}
                              aria-label={site.is_active ? 'Actif' : 'Inactif'}
                            />
                            {central ? (
                              <span className="st-central-pill" title="Pharmacie centrale SMIE">
                                Centrale
                              </span>
                            ) : null}
                          </div>
                          {site.address ? (
                            <span className="st-site-sub">{site.address}</span>
                          ) : (
                            <span className="st-site-sub st-site-sub--muted">Adresse non renseignée</span>
                          )}
                          <div className="st-site-meta">
                            <SiteCareModeBadge mode={site.site_care_mode} variant="compact" showIcon />
                            {hasPharmacy ? (
                              <span className="st-pharmacy-pill st-pharmacy-pill--yes">Pharmacie</span>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td data-label="Contact">
                        <div className="st-contact-stack">
                          <span className={site.contact_person ? 'st-contact-line' : 'st-contact-line st-contact-line--muted'}>
                            {site.contact_person || '—'}
                          </span>
                          {site.phone ? (
                            <a href={`tel:${site.phone}`} className="st-contact-line">
                              {site.phone}
                            </a>
                          ) : (
                            <span className="st-contact-line st-contact-line--muted">—</span>
                          )}
                        </div>
                      </td>
                      <td className="text-end adm-mobile-card-actions" data-label="Actions">
                        <button
                          type="button"
                          className={`co-action-btn adm-action-btn adm-mobile-action-btn ${
                            actionMenuSite?.id === site.id ? 'is-open' : ''
                          }`}
                          onClick={(event) => openActionMenu(event, site)}
                          aria-label={`Actions pour ${site.name}`}
                          aria-expanded={actionMenuSite?.id === site.id}
                          aria-haspopup="menu"
                          title="Actions"
                        >
                          <Icon name="ellipsis-vertical" size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SiteRowActionMenu
        anchorEl={actionMenuAnchor}
        site={actionMenuSite}
        onClose={closeActionMenu}
        onEdit={openEditModal}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDelete}
      />

      {showModal && (
        <div className="co-modal-overlay" role="presentation" onClick={() => !saving && setShowModal(false)}>
          <div
            className="co-modal st-site-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="st-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="co-modal__header">
              <div>
                <span className="co-modal__badge">{editingSite ? 'Modification' : 'Nouvelle fiche'}</span>
                <h2 id="st-modal-title">{editingSite ? 'Modifier le site' : 'Créer un site'}</h2>
              </div>
              <button
                type="button"
                className="co-modal__close"
                onClick={() => !saving && setShowModal(false)}
                aria-label="Fermer"
                disabled={saving}
              >
                <Icon name="times" size={18} />
              </button>
            </header>

            <form onSubmit={handleSubmit} className="co-modal__body">
              <div className="co-form-grid">
                <div className="co-form-group co-form-group--full">
                  <label htmlFor="st-name">
                    <Icon name="home" size={14} />
                    Nom du site *
                  </label>
                  <input
                    type="text"
                    id="st-name"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="Ex. Site Antananarivo Nord"
                    autoFocus
                  />
                </div>

                <div className="co-form-group co-form-group--full">
                  <label htmlFor="st-address">Adresse</label>
                  <input
                    type="text"
                    id="st-address"
                    value={formData.address}
                    onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                    placeholder="Rue, quartier, commune…"
                  />
                </div>

                <div className="co-form-group">
                  <label htmlFor="st-contact">
                    <Icon name="user" size={14} />
                    Personne contact
                  </label>
                  <input
                    type="text"
                    id="st-contact"
                    value={formData.contact_person}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, contact_person: e.target.value }))
                    }
                    placeholder="Responsable du site"
                  />
                </div>

                <div className="co-form-group">
                  <label htmlFor="st-phone">
                    <Icon name="phone" size={14} />
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="st-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="+261 …"
                  />
                </div>

                <div className="co-form-group co-form-group--full">
                  <span className="co-form-section-label">Mode de prise en charge *</span>
                  <div className="st-care-picker" role="radiogroup" aria-label="Mode métier du site">
                    {CARE_MODE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={formData.site_care_mode === option.value}
                        className={`st-care-picker__card st-care-picker__card--${option.variant} ${
                          formData.site_care_mode === option.value ? 'is-selected' : ''
                        }`}
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            site_care_mode: option.value,
                            has_local_pharmacy:
                              option.value === SITE_CARE_MODES.WITH_PHARMACY
                                ? true
                                : prev.has_local_pharmacy
                          }))
                        }
                      >
                        <span className="st-care-picker__icon">
                          <Icon name={option.icon} size={18} aria-hidden />
                        </span>
                        <span className="st-care-picker__title">{option.shortLabel}</span>
                        <p className="st-care-picker__desc">{option.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  className={`co-form-group co-form-group--full st-form-switch-wrap ${
                    pharmacyLocked ? 'is-locked' : ''
                  }`}
                >
                  <label htmlFor="st-local-pharmacy">
                    <input
                      type="checkbox"
                      id="st-local-pharmacy"
                      checked={pharmacyLocked ? true : Boolean(formData.has_local_pharmacy)}
                      disabled={pharmacyLocked}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, has_local_pharmacy: e.target.checked }))
                      }
                    />
                    Pharmacie locale disponible
                    {pharmacyLocked ? (
                      <span className="small fw-normal text-muted"> (imposé par le mode sélectionné)</span>
                    ) : null}
                  </label>
                </div>

                {editingSite && isCentralSite(editingSite) ? (
                  <div className="co-form-group co-form-group--full st-form-switch-wrap">
                    <label htmlFor="st-duty-cabinet">
                      <input
                        type="checkbox"
                        id="st-duty-cabinet"
                        checked={Boolean(formData.has_duty_cabinet)}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, has_duty_cabinet: e.target.checked }))
                        }
                      />
                      Armoire de garde (stock séparée pour nurse / major)
                      <span className="small fw-normal text-muted d-block mt-1">
                        Réservé au site pharmacie centrale — réassort depuis le stock principal.
                      </span>
                    </label>
                  </div>
                ) : null}
              </div>

              <footer className="co-modal__footer">
                <button
                  type="button"
                  className="co-btn-ghost"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button type="submit" className="co-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement…' : editingSite ? 'Enregistrer' : 'Créer le site'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
